import React from 'react';
import { Quote, Star } from 'lucide-react';

interface TestimonialData {
  quote: string;
  author: string;
  position: string;
  college: string;
  logo: string;
  logoColor: string;
}

export const EducatorTestimonials = () => {
  const testimonials: TestimonialData[] = [
    {
      quote: "A2Z Assist's session was not just a talk—it sparked direction and ambition in our students. The practical approach to career development was exactly what our engineering students needed.",
      author: "Dr. R.P. Reddy",
      position: "Principal",
      college: "MS Engineering College",
      logo: "M",
      logoColor: "#6B4423"
    },
    {
      quote: "The clarity and curiosity-driven approach made a huge impact on our students. Veeranath's journey from first employee to successful entrepreneur inspired many to think beyond traditional career paths.",
      author: "Dr. Nataraj Urs",
      position: "Associate Prof, Admin and Branding Vertical Head, School of Electronics and Communication",
      college: "REVA University",
      logo: "R",
      logoColor: "#6B4423"
    }
  ];

  return (
    <section className="py-4 sm:py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
      <div className="container mx-auto px-3 sm:px-4 md:px-6">
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <div className="inline-flex items-center px-3 sm:px-4 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-2 sm:mb-2 md:mb-3" style={{borderColor: '#C4A052', color: '#6B4423'}}>
            <img 
              src="/A2ZAssist%20logo.png" 
              alt="A2Z Assist Logo" 
              className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
            />
            Educator Endorsements
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 md:mb-4 px-2" style={{color: '#2C2420'}}>
            What Educators Say
          </h2>
          <p className="text-sm sm:text-base md:text-base max-w-4xl mx-auto px-3" style={{color: '#2C2420'}}>
            Feedback from professors, principals, and academic leaders who've experienced 
            the transformative impact of our A2ZCampus™ workshops firsthand.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 max-w-7xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 group hover:-translate-y-2">
              <div className="flex items-center mb-2 sm:mb-3 md:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center mr-2 sm:mr-2 md:mr-3 shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0" style={{backgroundColor: testimonial.logoColor}}>
                  <span className="text-white font-bold text-sm sm:text-base md:text-lg">{testimonial.logo}</span>
                </div>
                <div className="rounded-full p-1 sm:p-1 md:p-2 flex-shrink-0" style={{backgroundColor: '#C4A052'}}>
                  <Quote className="w-3 h-3 sm:w-3 sm:h-3 md:w-4 md:h-4 text-white" />
                </div>
                <div className="flex ml-2 sm:ml-2 md:ml-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 sm:w-3 sm:h-3 md:w-4 md:h-4 fill-current" style={{color: '#C4A052'}} />
                  ))}
                </div>
              </div>
              
              <blockquote className="mb-2 sm:mb-3 md:mb-4 italic text-xs sm:text-sm md:text-base leading-relaxed" style={{color: '#2C2420'}}>
                "{testimonial.quote}"
              </blockquote>
              
              <div className="border-t border-gray-200 pt-2 sm:pt-2 md:pt-3">
                <div className="flex items-center">
                  <div className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center mr-2 sm:mr-2 md:mr-3 flex-shrink-0" style={{backgroundColor: '#C4A052'}}>
                    <span className="text-white font-bold text-xs sm:text-sm md:text-base">
                      {testimonial.author.split(' ').map(name => name.charAt(0)).join('')}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-xs sm:text-sm md:text-base truncate" style={{color: '#2C2420'}}>{testimonial.author}</div>
                    <div className="text-xs md:text-xs font-medium leading-tight" style={{color: '#6B4423'}}>{testimonial.position}</div>
                    <div className="text-xs md:text-xs truncate" style={{color: '#2C2420'}}>{testimonial.college}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-6 sm:mt-8 md:mt-12 text-center">
          <div className="bg-white rounded-3xl p-3 sm:p-4 md:p-5 max-w-5xl mx-auto border-2 shadow-xl" style={{borderColor: '#C4A052'}}>
            <div className="flex flex-col sm:flex-row items-center justify-center mb-2 sm:mb-3 md:mb-4">
              <img 
                src="/A2ZAssist%20logo.png" 
                alt="A2Z Assist Logo" 
                className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mr-0 sm:mr-3 mb-2 sm:mb-0 object-contain"
              />
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-center sm:text-left" style={{color: '#2C2420'}}>
                Trusted by Leading Educational Institutions
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4 text-center">
              <div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 md:mb-1" style={{color: '#C4A052'}}>100%</div>
                <div className="font-medium text-xs sm:text-sm md:text-sm" style={{color: '#2C2420'}}>Positive Feedback</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 md:mb-1" style={{color: '#C4A052'}}>95%</div>
                <div className="font-medium text-xs sm:text-sm md:text-sm" style={{color: '#2C2420'}}>Return Invitations</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 md:mb-1" style={{color: '#C4A052'}}>500+</div>
                <div className="font-medium text-xs sm:text-sm md:text-sm" style={{color: '#2C2420'}}>Students Impacted</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};